import { assertNever, getUnsafeChainIdToDomain } from '@sparkdotfi/common-universal'

import { OngoingCampaignRow } from '../types'
import { getChainConfigEntry } from '@/config/chain'
import { SimplifiedQueryResult, transformSimplifiedQueryResult } from '@/domain/common/query'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { OngoingCampaign } from '@/features/spark-rewards/data/ongoingCampaignsQueryOptions'
import { useOngoingCampaignsQuery } from '@/features/spark-rewards/data/useOngoingCampaignsQuery'
import { InternalLink, Link } from '@/routing/types'
import { assets } from '@/ui/assets'

export type UseOngoingCampaignsResult = SimplifiedQueryResult<OngoingCampaignRow[]>

export function useOngoingCampaigns(): UseOngoingCampaignsResult {
  const ongoingCampaignsResult = useOngoingCampaignsQuery()

  return transformSimplifiedQueryResult(ongoingCampaignsResult, (data) =>
    data.map((campaign) => {
      const chainId = 'chainId' in campaign ? campaign.chainId : campaign.rewardChainId
      const involvedTokensSymbols =
        campaign.type === 'sparklend'
          ? [...campaign.depositTokenSymbols, ...campaign.borrowTokenSymbols]
          : campaign.type === 'savings'
            ? campaign.savingsTokenSymbols
            : []

      return {
        ...campaign,
        involvedTokensSymbols,
        involvedTokensLogos: involvedTokensSymbols.map((symbol) => getTokenLogo(chainId, symbol)),
        rewardTokenLogo: getTokenLogo(campaign.rewardChainId, campaign.rewardTokenSymbol),
        startLink: getStartLink(campaign),
      }
    }),
  )
}

function getTokenLogo(chainId: number, symbol: TokenSymbol): string {
  const chainConfig = getChainConfigEntry(chainId)
  const token = chainConfig.tokenDefinitionRepository.findBySymbol(symbol)
  return token?.logo ?? assets.token.unknown
}

function getStartLink(campaign: OngoingCampaign): Link {
  if (campaign.type === 'external' || campaign.type === 'social') {
    return campaign.link
  }

  if (campaign.type === 'sparklend') {
    if (campaign.depositTokenAddresses.length === 1 && campaign.borrowTokenAddresses.length === 0) {
      return InternalLink({
        to: '/markets/$chainId/$asset',
        params: {
          chainId: campaign.chainId,
          asset: campaign.depositTokenAddresses[0]!,
        },
      })
    }

    if (campaign.depositTokenAddresses.length === 0 && campaign.borrowTokenAddresses.length === 1) {
      return InternalLink({
        to: '/markets/$chainId/$asset',
        params: {
          chainId: campaign.chainId,
          asset: campaign.borrowTokenAddresses[0]!,
        },
      })
    }

    return InternalLink({
      to: '/my-portfolio',
    })
  }

  if (campaign.type === 'savings') {
    if (campaign.savingsTokenAddresses.length === 1) {
      const { savings } = getChainConfigEntry(campaign.chainId)
      const savingsTokenSymbolIsInConfig =
        savings?.accounts?.some((account) => account.savingsToken === campaign.savingsTokenSymbols[0]) ?? false
      if (savingsTokenSymbolIsInConfig) {
        return InternalLink({
          to: '/savings/$domain/$savingsToken',
          params: {
            domain: getUnsafeChainIdToDomain(campaign.chainId),
            savingsToken: campaign.savingsTokenSymbols[0]!.toLowerCase(),
          },
        })
      }
    }

    return InternalLink({
      to: '/',
    })
  }

  assertNever(campaign)
}
